// ═══════════════════════════════════════════════════════════════════════════════
// AUTOMATED FOLLOW-UP ENGINE v1.0
// EnergiPro Solutions — RFQ & Proposal Workflow
// ═══════════════════════════════════════════════════════════════════════════════
// Schedule: Day 1 → Day 3 → Day 7 → Day 14
// Sender: rfq@energiprogroup.com
// Stop Conditions: Client reply, RFQ Closed/Won/Lost
// ═══════════════════════════════════════════════════════════════════════════════

// ─── TYPES ───────────────────────────────────────────────────────────────────

export type FollowUpStage = 0 | 1 | 2 | 3 | 4; // 0 = not started, 1-4 = stages
export type ClientResponseStatus = 'awaiting' | 'replied' | 'no-response' | 'declined';
export type FollowUpStatus = 'active' | 'paused' | 'completed' | 'stopped';
export type RFQCloseReason = 'won' | 'lost' | 'closed' | 'client-replied';
export type RFQPriorityLevel = 'high' | 'medium' | 'low';

export interface FollowUpSchedule {
  stage: 1 | 2 | 3 | 4;
  dayOffset: number;
  label: string;
  purpose: string;
}

export interface FollowUpTrackingRecord {
  rfqId: string;
  referenceId: string;
  clientName: string;
  clientEmail: string;
  companyName: string;
  serviceName: string;
  proposalSentDate: string; // ISO date when proposal email was sent
  lastEmailSentDate: string | null;
  followUpStage: FollowUpStage;
  clientResponseStatus: ClientResponseStatus;
  automationStatus: FollowUpStatus;
  pausedAt?: string;
  pausedBy?: string;
  stoppedReason?: RFQCloseReason;
  history: FollowUpLogEntry[];
  // Priority scoring fields
  rfqScore?: number; // 0-100
  priority?: RFQPriorityLevel;
  priorityOverride?: RFQPriorityLevel; // Manual override
  priorityOverrideBy?: string;
  priorityOverrideAt?: string;
  // Scoring input data
  service?: string;
  location?: string;
  timeline?: string;
  h2sPresence?: string;
  wellheadPressure?: string;
  sandProduction?: string;
  clientType?: string;
}

export interface FollowUpLogEntry {
  id: string;
  timestamp: string;
  action: 'email_sent' | 'email_failed' | 'paused' | 'resumed' | 'stopped' | 'manual_send' | 'client_replied' | 'status_changed';
  stage?: FollowUpStage;
  description: string;
  deliveryStatus?: 'sent' | 'delivered' | 'failed' | 'bounced';
  error?: string;
  actor: string; // 'system' or admin name
}

export interface FollowUpAction {
  type: 'send_followup' | 'pause' | 'resume' | 'stop' | 'manual_send';
  rfqId: string;
  stage?: FollowUpStage;
  reason?: string;
}

// ─── CONSTANTS ───────────────────────────────────────────────────────────────

export const SENDER_EMAIL = 'rfq@energiprogroup.com';

export const FOLLOW_UP_SCHEDULE: FollowUpSchedule[] = [
  { stage: 1, dayOffset: 1, label: 'Confirmation', purpose: 'Confirm proposal receipt and availability for questions' },
  { stage: 2, dayOffset: 3, label: 'Engagement', purpose: 'Offer technical clarification and highlight time-sensitive items' },
  { stage: 3, dayOffset: 7, label: 'Urgency', purpose: 'Emphasize equipment/crew availability window and request decision timeline' },
  { stage: 4, dayOffset: 14, label: 'Final Follow-Up', purpose: 'Final outreach — request go/no-go or feedback for file closure' },
];

// Priority-based follow-up schedules
export const HIGH_PRIORITY_FOLLOW_UP: FollowUpSchedule[] = [
  { stage: 1, dayOffset: 1, label: 'Immediate Follow-Up', purpose: 'Confirm receipt, priority handling, offer same-day callback' },
  { stage: 2, dayOffset: 2, label: 'Escalation', purpose: 'Escalation email + flag for manual senior contact' },
  { stage: 3, dayOffset: 4, label: 'Urgency Push', purpose: 'Limited availability notice — require decision timeline' },
];

export const LOW_PRIORITY_FOLLOW_UP: FollowUpSchedule[] = [
  { stage: 1, dayOffset: 3, label: 'Initial Contact', purpose: 'Confirm proposal receipt and offer support' },
  { stage: 2, dayOffset: 7, label: 'Check-In', purpose: 'Gentle follow-up on decision timeline' },
  { stage: 3, dayOffset: 14, label: 'Final Outreach', purpose: 'Final check — request feedback or close file' },
];

/**
 * Get the appropriate follow-up schedule based on RFQ priority
 */
export function getScheduleForPriority(priority: RFQPriorityLevel): FollowUpSchedule[] {
  switch (priority) {
    case 'high': return HIGH_PRIORITY_FOLLOW_UP;
    case 'low': return LOW_PRIORITY_FOLLOW_UP;
    default: return FOLLOW_UP_SCHEDULE; // Medium uses standard
  }
}

/**
 * Get effective priority (manual override takes precedence)
 */
export function getEffectivePriority(record: FollowUpTrackingRecord): RFQPriorityLevel {
  return record.priorityOverride || record.priority || 'medium';
}

/**
 * Get max stages for a given priority level
 */
export function getMaxStagesForPriority(priority: RFQPriorityLevel): number {
  return getScheduleForPriority(priority).length;
}

// RFQ statuses that STOP all automation
const STOP_STATUSES = ['Closed', 'Approved', 'Rejected', 'Won', 'Lost'];

// ─── CORE ENGINE ─────────────────────────────────────────────────────────────

/**
 * Determines if automation should be stopped based on current RFQ status
 */
export function shouldStopAutomation(rfqStatus: string, clientResponseStatus: ClientResponseStatus): boolean {
  if (STOP_STATUSES.includes(rfqStatus)) return true;
  if (clientResponseStatus === 'replied' || clientResponseStatus === 'declined') return true;
  return false;
}

/**
 * Calculate the next follow-up date based on proposal sent date, stage, and priority
 */
export function getNextFollowUpDate(proposalSentDate: string, stage: FollowUpStage, priority?: RFQPriorityLevel): Date | null {
  const schedule = getScheduleForPriority(priority || 'medium');
  const maxStages = schedule.length;
  if (stage >= maxStages) return null; // All follow-ups completed
  const nextStage = schedule[stage]; // stage 0 → index 0 (stage 1), stage 1 → index 1 (stage 2), etc.
  if (!nextStage) return null;
  const sentDate = new Date(proposalSentDate);
  const followUpDate = new Date(sentDate.getTime() + nextStage.dayOffset * 24 * 60 * 60 * 1000);
  return followUpDate;
}

/**
 * Check if a follow-up is due (current time >= scheduled time)
 */
export function isFollowUpDue(record: FollowUpTrackingRecord): boolean {
  if (record.automationStatus !== 'active') return false;
  const priority = getEffectivePriority(record);
  const maxStages = getMaxStagesForPriority(priority);
  if (record.followUpStage >= maxStages) return false;

  const nextDate = getNextFollowUpDate(record.proposalSentDate, record.followUpStage, priority);
  if (!nextDate) return false;

  return new Date() >= nextDate;
}

/**
 * Get the current stage info for a tracking record
 */
export function getCurrentStageInfo(record: FollowUpTrackingRecord): FollowUpSchedule | null {
  const priority = getEffectivePriority(record);
  const schedule = getScheduleForPriority(priority);
  if (record.followUpStage === 0) return schedule[0];
  const maxStages = schedule.length;
  if (record.followUpStage >= maxStages) return null;
  return schedule[record.followUpStage] || null;
}

/**
 * Get next stage info
 */
export function getNextStageInfo(record: FollowUpTrackingRecord): FollowUpSchedule | null {
  const priority = getEffectivePriority(record);
  const schedule = getScheduleForPriority(priority);
  const maxStages = schedule.length;
  if (record.followUpStage >= maxStages) return null;
  return schedule[record.followUpStage] || null;
}

/**
 * Process a follow-up action and return updated record + log entry
 */
export function processFollowUpAction(
  record: FollowUpTrackingRecord,
  action: FollowUpAction,
  actor: string = 'system'
): { updatedRecord: FollowUpTrackingRecord; logEntry: FollowUpLogEntry } {
  const now = new Date().toISOString();
  let updatedRecord = { ...record };
  let logEntry: FollowUpLogEntry;

  switch (action.type) {
    case 'send_followup': {
      const priority = getEffectivePriority(record);
      const maxStages = getMaxStagesForPriority(priority);
      const schedule = getScheduleForPriority(priority);
      const newStage = (record.followUpStage + 1) as FollowUpStage;
      updatedRecord.followUpStage = newStage;
      updatedRecord.lastEmailSentDate = now;
      if (newStage >= maxStages) {
        updatedRecord.automationStatus = 'completed';
        updatedRecord.clientResponseStatus = 'no-response';
      }
      logEntry = {
        id: `log-${record.rfqId}-${Date.now()}`,
        timestamp: now,
        action: 'email_sent',
        stage: newStage,
        description: `Follow-up #${newStage} (${schedule[newStage - 1]?.label}) sent to ${record.clientEmail}${priority === 'high' ? ' [HIGH PRIORITY]' : ''}`,
        deliveryStatus: 'sent',
        actor,
      };
      break;
    }
    case 'pause': {
      updatedRecord.automationStatus = 'paused';
      updatedRecord.pausedAt = now;
      updatedRecord.pausedBy = actor;
      logEntry = {
        id: `log-${record.rfqId}-${Date.now()}`,
        timestamp: now,
        action: 'paused',
        description: `Follow-up automation paused by ${actor}. Reason: ${action.reason || 'Manual pause'}`,
        actor,
      };
      break;
    }
    case 'resume': {
      updatedRecord.automationStatus = 'active';
      updatedRecord.pausedAt = undefined;
      updatedRecord.pausedBy = undefined;
      logEntry = {
        id: `log-${record.rfqId}-${Date.now()}`,
        timestamp: now,
        action: 'resumed',
        description: `Follow-up automation resumed by ${actor}`,
        actor,
      };
      break;
    }
    case 'stop': {
      updatedRecord.automationStatus = 'stopped';
      updatedRecord.stoppedReason = (action.reason as RFQCloseReason) || 'closed';
      logEntry = {
        id: `log-${record.rfqId}-${Date.now()}`,
        timestamp: now,
        action: 'stopped',
        description: `Follow-up automation stopped. Reason: ${action.reason || 'Manual stop'}`,
        actor,
      };
      break;
    }
    case 'manual_send': {
      const manualStage = (action.stage || record.followUpStage + 1) as FollowUpStage;
      updatedRecord.lastEmailSentDate = now;
      logEntry = {
        id: `log-${record.rfqId}-${Date.now()}`,
        timestamp: now,
        action: 'manual_send',
        stage: manualStage,
        description: `Manual follow-up sent by ${actor} to ${record.clientEmail}`,
        deliveryStatus: 'sent',
        actor,
      };
      break;
    }
    default: {
      logEntry = {
        id: `log-${record.rfqId}-${Date.now()}`,
        timestamp: now,
        action: 'status_changed',
        description: `Unknown action: ${action.type}`,
        actor,
      };
    }
  }

  updatedRecord.history = [...record.history, logEntry];
  return { updatedRecord, logEntry };
}

/**
 * Mark client as replied — stops all automation
 */
export function markClientReplied(record: FollowUpTrackingRecord): FollowUpTrackingRecord {
  const now = new Date().toISOString();
  return {
    ...record,
    clientResponseStatus: 'replied',
    automationStatus: 'stopped',
    stoppedReason: 'client-replied',
    history: [
      ...record.history,
      {
        id: `log-${record.rfqId}-${Date.now()}`,
        timestamp: now,
        action: 'client_replied',
        description: 'Client response detected — all follow-up automation stopped',
        actor: 'system',
      },
    ],
  };
}

// ─── MOCK DATA GENERATOR ─────────────────────────────────────────────────────

export function generateMockFollowUpData(): FollowUpTrackingRecord[] {
  const now = new Date();

  return [
    {
      rfqId: 'RFQ-2026-0041',
      referenceId: 'EPI-RFQ-2026-0041',
      clientName: 'Ahmed Al-Rashid',
      clientEmail: 'ahmed.rashid@aramco-contractors.com',
      companyName: 'Aramco Contractors Ltd',
      serviceName: 'Surface Well Testing — 10K PSI Separator Package',
      proposalSentDate: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      lastEmailSentDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      followUpStage: 1,
      clientResponseStatus: 'awaiting',
      automationStatus: 'active',
      rfqScore: 92,
      priority: 'high',
      service: 'surface-well-testing',
      location: 'ksa',
      timeline: 'immediate',
      h2sPresence: 'yes',
      wellheadPressure: '10000',
      sandProduction: 'high',
      clientType: 'operator',
      history: [
        {
          id: 'log-0041-1',
          timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 1,
          description: 'Follow-up #1 (Immediate Follow-Up) sent to ahmed.rashid@aramco-contractors.com [HIGH PRIORITY]',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
      ],
    },
    {
      rfqId: 'RFQ-2026-0038',
      referenceId: 'EPI-RFQ-2026-0038',
      clientName: 'James Morrison',
      clientEmail: 'j.morrison@adnoc-services.ae',
      companyName: 'ADNOC Services',
      serviceName: 'Nitrogen Pumping — HP Unit + Frac Tanks',
      proposalSentDate: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      lastEmailSentDate: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      followUpStage: 2,
      clientResponseStatus: 'awaiting',
      automationStatus: 'active',
      rfqScore: 68,
      priority: 'medium',
      service: 'stimulation-nitrogen',
      location: 'uae',
      timeline: '1-2-weeks',
      h2sPresence: 'no',
      wellheadPressure: '4200',
      sandProduction: 'low',
      clientType: 'operator',
      history: [
        {
          id: 'log-0038-1',
          timestamp: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 1,
          description: 'Follow-up #1 (Confirmation) sent to j.morrison@adnoc-services.ae',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
        {
          id: 'log-0038-2',
          timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 2,
          description: 'Follow-up #2 (Engagement) sent to j.morrison@adnoc-services.ae',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
      ],
    },
    {
      rfqId: 'RFQ-2026-0035',
      referenceId: 'EPI-RFQ-2026-0035',
      clientName: 'Khalid Mansour',
      clientEmail: 'k.mansour@petronas-mena.com',
      companyName: 'PETRONAS MENA Operations',
      serviceName: 'Custom Fabrication — Test Separator Skid (ASME)',
      proposalSentDate: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000).toISOString(),
      lastEmailSentDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      followUpStage: 3,
      clientResponseStatus: 'awaiting',
      automationStatus: 'active',
      rfqScore: 62,
      priority: 'medium',
      service: 'fabrication-skid',
      location: 'egypt',
      timeline: 'planned',
      h2sPresence: 'no',
      wellheadPressure: '',
      sandProduction: '',
      clientType: 'epc',
      history: [
        {
          id: 'log-0035-1',
          timestamp: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 1,
          description: 'Follow-up #1 (Confirmation) sent to k.mansour@petronas-mena.com',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
        {
          id: 'log-0035-2',
          timestamp: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 2,
          description: 'Follow-up #2 (Engagement) sent to k.mansour@petronas-mena.com',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
        {
          id: 'log-0035-3',
          timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 3,
          description: 'Follow-up #3 (Urgency) sent to k.mansour@petronas-mena.com',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
      ],
    },
    {
      rfqId: 'RFQ-2026-0032',
      referenceId: 'EPI-RFQ-2026-0032',
      clientName: 'Sarah Williams',
      clientEmail: 's.williams@total-energies.eg',
      companyName: 'TotalEnergies Egypt',
      serviceName: 'Oilfield Chemicals — H₂S Scavenger + Corrosion Inhibitor',
      proposalSentDate: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      lastEmailSentDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      followUpStage: 4,
      clientResponseStatus: 'no-response',
      automationStatus: 'completed',
      rfqScore: 55,
      priority: 'medium',
      service: 'chemicals-supply',
      location: 'egypt',
      timeline: 'planned',
      h2sPresence: 'yes',
      wellheadPressure: '',
      sandProduction: 'medium',
      clientType: 'operator',
      history: [
        {
          id: 'log-0032-1',
          timestamp: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 1,
          description: 'Follow-up #1 (Confirmation) sent',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
        {
          id: 'log-0032-2',
          timestamp: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 2,
          description: 'Follow-up #2 (Engagement) sent',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
        {
          id: 'log-0032-3',
          timestamp: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 3,
          description: 'Follow-up #3 (Urgency) sent',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
        {
          id: 'log-0032-4',
          timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 4,
          description: 'Follow-up #4 (Final Follow-Up) sent',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
      ],
    },
    {
      rfqId: 'RFQ-2026-0039',
      referenceId: 'EPI-RFQ-2026-0039',
      clientName: 'Omar Al-Fahad',
      clientEmail: 'omar.fahad@sabic-contractors.sa',
      companyName: 'SABIC Contractors',
      serviceName: 'Heavy Equipment Rental — 50T Crane + Generator Package',
      proposalSentDate: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000).toISOString(),
      lastEmailSentDate: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      followUpStage: 1,
      clientResponseStatus: 'replied',
      automationStatus: 'stopped',
      stoppedReason: 'client-replied',
      rfqScore: 42,
      priority: 'low',
      service: 'equipment-rental',
      location: 'ksa',
      timeline: 'planned',
      h2sPresence: 'no',
      wellheadPressure: '',
      sandProduction: '',
      clientType: 'epc',
      history: [
        {
          id: 'log-0039-1',
          timestamp: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 1,
          description: 'Follow-up #1 (Initial Contact) sent',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
        {
          id: 'log-0039-2',
          timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'client_replied',
          description: 'Client response detected — all follow-up automation stopped',
          actor: 'system',
        },
      ],
    },
    {
      rfqId: 'RFQ-2026-0040',
      referenceId: 'EPI-RFQ-2026-0040',
      clientName: 'David Chen',
      clientEmail: 'd.chen@schlumberger-mena.com',
      companyName: 'SLB MENA Operations',
      serviceName: 'Filtration Systems — Produced Water Treatment Skid',
      proposalSentDate: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      lastEmailSentDate: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      followUpStage: 1,
      clientResponseStatus: 'awaiting',
      automationStatus: 'paused',
      pausedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      pausedBy: 'Admin',
      rfqScore: 56,
      priority: 'medium',
      service: 'filtration-water',
      location: 'uae',
      timeline: '1-2-weeks',
      h2sPresence: 'no',
      wellheadPressure: '2800',
      sandProduction: 'medium',
      clientType: 'vendor',
      history: [
        {
          id: 'log-0040-1',
          timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'email_sent',
          stage: 1,
          description: 'Follow-up #1 (Confirmation) sent',
          deliveryStatus: 'delivered',
          actor: 'system',
        },
        {
          id: 'log-0040-2',
          timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'paused',
          description: 'Follow-up automation paused by Admin. Reason: Client requested callback',
          actor: 'Admin',
        },
      ],
    },
    {
      rfqId: 'RFQ-2026-0042',
      referenceId: 'EPI-RFQ-2026-0042',
      clientName: 'Abdullah Al-Dosari',
      clientEmail: 'a.dosari@saudi-aramco.com',
      companyName: 'Saudi Aramco — Field Ops',
      serviceName: 'Surface Well Testing — 10K PSI HP Package (H₂S)',
      proposalSentDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      lastEmailSentDate: null,
      followUpStage: 0,
      clientResponseStatus: 'awaiting',
      automationStatus: 'active',
      rfqScore: 97,
      priority: 'high',
      service: 'surface-well-testing',
      location: 'ksa',
      timeline: 'immediate',
      h2sPresence: 'yes',
      wellheadPressure: '10500',
      sandProduction: 'high',
      clientType: 'operator',
      history: [],
    },
    {
      rfqId: 'RFQ-2026-0043',
      referenceId: 'EPI-RFQ-2026-0043',
      clientName: 'Tariq Shaheen',
      clientEmail: 't.shaheen@logistics-co.sa',
      companyName: 'Al Rajhi Industrial',
      serviceName: 'Logistics & Transport — Equipment Mobilization',
      proposalSentDate: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000).toISOString(),
      lastEmailSentDate: null,
      followUpStage: 0,
      clientResponseStatus: 'awaiting',
      automationStatus: 'active',
      rfqScore: 35,
      priority: 'low',
      service: 'logistics-transport',
      location: 'ksa',
      timeline: 'planned',
      h2sPresence: 'no',
      wellheadPressure: '',
      sandProduction: '',
      clientType: 'vendor',
      history: [],
    },
  ];
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

export function formatDaysAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export function formatNextFollowUp(record: FollowUpTrackingRecord): string {
  if (record.automationStatus !== 'active') return '—';
  const priority = getEffectivePriority(record);
  const maxStages = getMaxStagesForPriority(priority);
  if (record.followUpStage >= maxStages) return 'Completed';
  const nextDate = getNextFollowUpDate(record.proposalSentDate, record.followUpStage, priority);
  if (!nextDate) return '—';
  const diff = nextDate.getTime() - Date.now();
  if (diff <= 0) return 'Due now';
  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours < 24) return `In ${hours}h`;
  const days = Math.floor(hours / 24);
  return `In ${days}d`;
}

export function getStageProgress(stage: FollowUpStage, priority?: RFQPriorityLevel): number {
  const maxStages = getMaxStagesForPriority(priority || 'medium');
  return (stage / maxStages) * 100;
}

/**
 * Override the priority of a tracking record (manual override)
 */
export function overridePriority(
  record: FollowUpTrackingRecord,
  newPriority: RFQPriorityLevel,
  actor: string
): FollowUpTrackingRecord {
  return {
    ...record,
    priorityOverride: newPriority,
    priorityOverrideBy: actor,
    priorityOverrideAt: new Date().toISOString(),
    history: [
      ...record.history,
      {
        id: `log-${record.rfqId}-${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: 'status_changed',
        description: `Priority manually overridden to ${newPriority.toUpperCase()} by ${actor}`,
        actor,
      },
    ],
  };
}
